/**
 * Wix Groups API – Headless integration.
 * Powers the Community page with domain communities for operators, engineers,
 * OEM partners, and safety professionals.
 *
 * Docs: https://dev.wix.com/docs/rest/api-reference/groups
 */
import { wixFetch } from "./client";

export interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  category: "domain" | "role" | "cohort";
}

/** Fetch all groups from Wix. */
export async function getGroups(): Promise<Group[]> {
  const res = await wixFetch<{ groups: Group[] }>("/groups/v1/groups", {
    method: "GET",
  });
  return res?.groups ?? [];
}

/** Seeded community groups used when Wix Groups is not configured. */
export const COMMUNITY_GROUPS: Group[] = [
  {
    id: "g-automotive",
    name: "Automotive Safety Community",
    description:
      "Operators and engineers working on automotive safety domains — ADAS, braking, and autonomous intervention.",
    memberCount: 1240,
    category: "domain",
  },
  {
    id: "g-aviation",
    name: "Aviation Safety Community",
    description:
      "Aerospace and aviation safety professionals discussing flight-domain diagnostics and intervention templates.",
    memberCount: 890,
    category: "domain",
  },
  {
    id: "g-defense",
    name: "Defense & Military Community",
    description:
      "Restricted community for defense operators with classified-domain templates and mission support discussions.",
    memberCount: 420,
    category: "domain",
  },
  {
    id: "g-energy",
    name: "Energy & Utilities Community",
    description:
      "Grid operators and energy engineers covering predictive failure detection and grid intervention.",
    memberCount: 670,
    category: "domain",
  },
  {
    id: "g-oem-partners",
    name: "OEM Partner Network",
    description:
      "OEM and Tier-1 partners sharing integration patterns, SDK updates, and co-branded certification tracks.",
    memberCount: 310,
    category: "role",
  },
  {
    id: "g-operators",
    name: "Sentinel Operators Forum",
    description:
      "General operator forum for best practices, incident sharing, and intervention template discussions.",
    memberCount: 2100,
    category: "role",
  },
  {
    id: "g-engineers",
    name: "Diagnostic Engineers Guild",
    description:
      "Engineers building diagnostic profiles, tuning learning loops, and engineering intervention libraries.",
    memberCount: 540,
    category: "role",
  },
  {
    id: "g-cohort-q3",
    name: "Q3 Training Cohort",
    description:
      "Active training cohort progressing through core and advanced certification tracks together.",
    memberCount: 180,
    category: "cohort",
  },
];
